import { createSignal, Show } from 'solid-js';
import { Button, Text } from '@components';
import API from '@util/api';

export default function CloudflareSetup(props) {
	const [token, setToken] = createSignal('');
	const [matchedZone, setMatchedZone] = createSignal(null);
	const [siteHost, setSiteHost] = createSignal('');
	const [step, setStep] = createSignal(1);
	const [loading, setLoading] = createSignal('');
	const [error, setError] = createSignal('');

	const verifyToken = async () => {
		if (!token().trim()) {
			setError('Please enter your API token');
			return;
		}

		setLoading('Verifying token...');
		setError('');

		try {
			const res = await API.post('cf/verify-token', { token: token() });
			console.log('CF verify-token response:', res.data);
			if (res.data?.success) {
				// Fetch zones after token is verified
				const zonesRes = await API.get('cf/zones');
				console.log('CF zones response:', zonesRes.data);

				setSiteHost(zonesRes.data.site_host || '');

				if (zonesRes.data?.matched_zone) {
					setMatchedZone(zonesRes.data.matched_zone);
					setStep(2);
				} else {
					setError(
						`Your site domain (${zonesRes.data.site_host}) is not found in your Cloudflare account. ` +
						'Please add your domain to Cloudflare and ensure DNS is proxied (orange cloud).'
					);
				}
			} else {
				const debugInfo = res.data?.debug ? ` (Debug: ${JSON.stringify(res.data.debug)})` : '';
				setError((res.data?.message || 'Failed to verify token') + debugInfo);
			}
		} catch (err) {
			console.error('CF API error:', err);
			setError('Connection error. Please try again.');
		} finally {
			setLoading('');
		}
	};

	const deploy = async () => {
		const zone = matchedZone();
		if (!zone) {
			setError('No matching zone found');
			return;
		}

		setLoading('Deploying cache worker...');
		setError('');

		try {
			const res = await API.post('cf/deploy', { zone_id: zone.id });
			console.log('CF deploy response:', res.data);
			if (res.data?.success) {
				props.onConnected?.();
			} else {
				setError(res.data?.message || 'Failed to deploy');
			}
		} catch (err) {
			console.error('CF deploy error:', err);
			setError('Connection error. Please try again.');
		} finally {
			setLoading('');
		}
	};

	return (
		<div class="ap-space-y-4">
			<Show when={error()}>
				<div class="ap-p-3 ap-bg-red-50 ap-border ap-border-red-200 ap-rounded-lg ap-text-sm ap-text-red-700">
					{error()}
				</div>
			</Show>

			<Show when={step() === 1}>
				<div class="ap-space-y-4">
					<div class="ap-space-y-2">
						<label class="ap-block ap-text-sm ap-font-medium ap-text-slate-700">
							Cloudflare API Token
						</label>
						<Text
							type="password"
							placeholder="Enter your Cloudflare API token"
							value={token()}
							onInput={(e) => setToken(e.target.value)}
						/>
						<p class="ap-text-xs ap-text-slate-500">
							Create a token with these permissions:{' '}
							<a
								href="https://dash.cloudflare.com/profile/api-tokens"
								target="_blank"
								rel="noopener noreferrer"
								class="ap-text-indigo-600 hover:ap-underline"
							>
								Create token
							</a>
						</p>
						<ul class="ap-text-xs ap-text-slate-500 ap-space-y-1 ap-ml-1">
							<li><code class="ap-bg-slate-100 ap-px-1 ap-rounded">Zone : Zone : Read</code></li>
							<li><code class="ap-bg-slate-100 ap-px-1 ap-rounded">Zone : Cache Purge : Purge</code></li>
							<li><code class="ap-bg-slate-100 ap-px-1 ap-rounded">Zone : Worker Routes : Edit</code></li>
							<li><code class="ap-bg-slate-100 ap-px-1 ap-rounded">Account : Workers Scripts : Edit</code></li>
						</ul>
					</div>

					<Button
						onClick={verifyToken}
						loading={loading()}
						disabled={!token().trim()}
					>
						Verify & Connect
					</Button>
				</div>
			</Show>

			<Show when={step() === 2}>
				<div class="ap-space-y-4">
					<div class="ap-p-3 ap-bg-green-50 ap-border ap-border-green-200 ap-rounded-lg ap-text-sm ap-text-green-700">
						Token verified. Found matching zone for your site.
					</div>

					<div class="ap-p-4 ap-bg-slate-50 ap-border ap-border-slate-200 ap-rounded-lg">
						<div class="ap-flex ap-items-center ap-gap-3">
							<div class="ap-flex-shrink-0 ap-w-10 ap-h-10 ap-bg-orange-100 ap-rounded-lg ap-flex ap-items-center ap-justify-center">
								<svg class="ap-w-5 ap-h-5 ap-text-orange-600" viewBox="0 0 24 24" fill="currentColor">
									<path d="M16.604 11.048a5.67 5.67 0 0 0 .012-.105l.015-.135a5.89 5.89 0 0 0 .069-.675c0-3.143-2.553-5.746-5.7-5.746-3.148 0-5.7 2.603-5.7 5.746 0 .238.016.472.046.702l.016.114c.01.078.022.155.036.232l.014.085c.025.131.054.26.089.387l.012.047c.036.129.077.256.122.382l.016.045c.047.127.098.252.154.375l.012.027c.057.123.118.244.184.362l.017.03a5.7 5.7 0 0 0 .213.355l.02.03c.074.107.152.21.233.312l.025.032c.08.1.165.196.252.29l.03.032c.088.094.18.185.274.273l.03.027c.097.089.197.175.3.258l.025.02c.107.086.217.168.33.247l.017.012a5.728 5.728 0 0 0 .77.44l.018.009c.125.062.253.12.383.173l.038.015c.127.051.257.098.388.14l.043.014c.133.042.268.08.404.113l.038.01c.144.034.29.062.437.086l.024.004c.16.025.322.044.485.055h5.7a3.6 3.6 0 1 0 0-7.2h-.696z"/>
								</svg>
							</div>
							<div class="ap-flex-1">
								<div class="ap-font-medium ap-text-slate-900">{matchedZone()?.name}</div>
								<div class="ap-text-sm ap-text-slate-500">
									Route: <code class="ap-bg-slate-200 ap-px-1 ap-rounded">{siteHost()}/*</code>
								</div>
							</div>
							<Show when={matchedZone()?.status === 'active'}>
								<span class="ap-text-xs ap-font-medium ap-px-2 ap-py-1 ap-bg-green-100 ap-text-green-700 ap-rounded">
									Active
								</span>
							</Show>
						</div>
					</div>

					<div class="ap-flex ap-gap-3">
						<Button
							variant="secondary"
							onClick={() => {
								setStep(1);
								setMatchedZone(null);
							}}
						>
							Back
						</Button>
						<Button
							onClick={deploy}
							loading={loading()}
						>
							Deploy Cache
						</Button>
					</div>
				</div>
			</Show>
		</div>
	);
}
